import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { VersionBadge } from "@/components/VersionBadge";
import { CookieNotice } from "@/components/CookieNotice";
import { openCookiePreferences } from "@/lib/consent";
import {
    ScanLine, CheckSquare, FileText, ClipboardList, Link2, ShieldCheck,
    ArrowRight,
} from "lucide-react";

const CAMPUS = "https://images.unsplash.com/photo-1613896527026-f195d5c818ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FINANCE = "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FEATURES = [
    { icon: FileText, title: "Purchase Requests", body: "Replace email and paper with a structured, trackable intake for every school purchase." },
    { icon: CheckSquare, title: "Multi-Level Approvals", body: "Route by amount, department, or GL account — with a complete, immutable audit trail." },
    { icon: ClipboardList, title: "Automatic Purchase Orders", body: "Branded PO PDFs issued to vendors the moment a request is fully approved." },
    { icon: ScanLine, title: "AI Invoice Scanning", body: "Upload a PDF or photo and GPT-5 reads every line item automatically, ready for review." },
    { icon: ShieldCheck, title: "3-Way Match", body: "Catch over-billing and coding errors before anything ever reaches your ledger." },
    { icon: Link2, title: "Veracross AP Posting", body: "Post the AP invoice, line items, GL codes and the scanned file in one click." },
];

const STEPS = ["Request", "Approve", "Purchase Order", "Scan Invoice", "3-Way Match", "Post to Veracross"];

export default function Landing() {
    return (
        <div className="min-h-screen bg-brand-bone">
            {/* Nav */}
            <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-border text-brand-emerald">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
                    <img src="/procureed-logo.png" alt="ProcureEd" className="h-8 w-auto" />
                    <nav className="ml-auto flex items-center gap-2">
                        <Link to="/login">
                            <Button className="bg-brand-teal hover:bg-sky-600 text-white rounded-full" data-testid="landing-signin-button">
                                Sign in
                            </Button>
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden bg-brand-bone text-brand-emerald">
                <div className="absolute inset-0 opacity-[0.12]">
                    <img src={CAMPUS} alt="School campus" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-bone via-brand-bone/90 to-brand-bone/50" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="max-w-2xl pe-fade-up">
                        <div className="mb-7">
                            <img src="/procureed-logo.png" alt="ProcureEd" className="h-14 sm:h-16 w-auto mb-4" data-testid="hero-procureed-logo" />
                            <div className="inline-flex items-center gap-3 rounded-full bg-white border border-emerald-100 pl-4 pr-5 py-2.5 shadow-sm" data-testid="partner-badge">
                                <img src="/veracross-logo.png" alt="Veracross" className="h-8 w-auto" />
                                <span className="h-5 w-px bg-border" />
                                <span className="text-sm font-semibold text-brand-emerald">Veracross Integration Partner</span>
                            </div>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            School purchasing,<br /> from request to
                            <span className="text-brand-teal"> posted invoice.</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                            ProcureEd automates the entire purchasing lifecycle — requests, multi-level approvals,
                            purchase orders, AI invoice scanning and 3-way match — then posts clean AP invoices
                            straight into Veracross Accounting.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/signup">
                                <Button size="lg" className="bg-brand-teal hover:bg-sky-600 text-white rounded-full px-7" data-testid="hero-cta-button">
                                    Get started <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            </Link>
                            <a href="#features">
                                <Button size="lg" variant="outline" className="rounded-full px-7 bg-transparent border-brand-emerald/30 text-brand-emerald hover:bg-emerald-50">
                                    See how it works
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Workflow strip */}
            <section className="border-b border-border bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3 text-sm">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-brand-bone px-3 py-1.5 font-medium text-brand-emerald">
                                    <span className="h-5 w-5 grid place-items-center rounded-full bg-brand-emerald text-white text-[11px]">{i + 1}</span>
                                    {s}
                                </span>
                                {i < STEPS.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="max-w-2xl">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-brand-emerald">
                        Everything your business office needs
                    </h2>
                    <p className="mt-3 text-muted-foreground text-base">
                        One platform in front of Veracross — so your ledger only ever receives approved, fully-coded, documented invoices.
                    </p>
                </div>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={f.title}
                                data-testid={`feature-card-${i}`}
                                className="bg-white border border-border rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(8,47,37,0.08)] hover:-translate-y-0.5 transition-all duration-200"
                            >
                                <div className="h-11 w-11 rounded-lg bg-brand-emerald/5 grid place-items-center text-brand-emerald mb-4">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="text-lg font-semibold tracking-tight text-brand-emerald">{f.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* OCR highlight */}
            <section className="bg-white border-y border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 text-brand-teal text-sm font-semibold mb-3">
                            <ScanLine className="h-4 w-4" /> AI LINE-ITEM OCR
                        </div>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-brand-emerald">
                            Every invoice line, read and coded automatically
                        </h2>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            Drop in a vendor invoice and GPT-5 extracts the vendor, dates, totals and every line item —
                            each with a confidence score. Your AP clerk confirms, the 3-way match runs, and it posts to
                            Veracross with the GL codes and source document attached.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm">
                            {["Reads PDFs and photos", "Structured line items with confidence", "Human review before posting", "Source file stored with the AP invoice"].map((t) => (
                                <li key={t} className="flex items-center gap-2 text-brand-emerald">
                                    <CheckSquare className="h-4 w-4 text-brand-teal" /> {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-border shadow-lg">
                        <img src={FINANCE} alt="Invoice scanning" className="w-full h-[340px] object-cover" />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-emerald-50 border-y border-emerald-100 text-brand-emerald">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Ready to modernize AP at your school?</h2>
                    <p className="mt-3 text-slate-600">Start a free trial and run a real invoice end-to-end in minutes.</p>
                    <Link to="/signup">
                        <Button size="lg" className="mt-7 bg-brand-teal hover:bg-sky-600 text-white rounded-full px-8" data-testid="cta-bottom-button">
                            Get started <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    </Link>
                </div>
            </section>

            <footer className="bg-white border-t border-border text-muted-foreground text-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                    <div className="flex flex-col items-center gap-3 text-center" data-testid="partner-lockup">
                        <div className="flex items-center gap-4">
                            <img src="/procureed-logo.png" alt="ProcureEd" className="h-7 w-auto" />
                            <span className="text-muted-foreground">×</span>
                            <img src="/veracross-logo.png" alt="Veracross" className="h-7 w-auto" />
                        </div>
                        <span className="text-xs font-medium text-brand-emerald">ProcureEd is a proud Veracross Integration Partner</span>
                    </div>
                    <div className="border-t border-border pt-6 flex flex-wrap items-start justify-between gap-3">
                        <div className="flex flex-col gap-0.5 max-w-xl">
                            <span>© {new Date().getFullYear()} ProcureEd · by Borrow A Geek Limited</span>
                            <span>18 Henry Street, Kilrush, Co. Clare V15 CX71, Ireland</span>
                            <span>+1 212 466 6905 · +353 65 671 2772</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <a href="/privacy" className="hover:text-brand-emerald transition-colors" data-testid="footer-privacy-link">Privacy &amp; Cookies</a>
                            <button onClick={openCookiePreferences} className="hover:text-brand-emerald transition-colors" data-testid="footer-cookie-prefs">Cookie preferences</button>
                            <VersionBadge />
                            <span>Integrated with Veracross Accounting</span>
                        </div>
                    </div>
                </div>
            </footer>
            <CookieNotice />
        </div>
    );
}
